import React, { useEffect, useState } from 'react';
import querystringify from 'querystringify';
import { isNil, find, isEmpty, has, startsWith, forEach } from 'lodash';
import { Link } from 'react-router-dom';

import { GET_USERS_IDP_URL } from '../../constants/api';
import LoginForm from './LoginForm';
import HomeDecorator from './HomeDecorator';
import GoogleLoginView from './GoogleLoginView';
import MicrosoftLoginView from './MicrosoftLoginView';
import OrgSwitchLoader from '../OrgSwitchLoader/OrgSwitchLoader';
import { GOOGLE_SSO_ENABLED, BASIC_LOGIN_ENABLED } from '../../constants/config';
import { GET_ENV_SSO_OPTIONS_URL } from 'js/constants/api';
import { SSO_AUTH_TYPES } from '../../constants/auth';
import { generateTokenIdentifiers } from '../../services/SSOAuthService';
import SSOLoginForm from './sso/SSOLoginForm';
import { isValidEmail } from '../../lib/ValidationUtils';
import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import { Button } from 'new/elements/Button';
import { clearSessionError, initGoogleApi } from 'js/actions';
import useAppDispatch from 'js/hooks/useAppDispatch';

import './Login.scss';

function LoginView(props) {
  const [state, set] = useState({
    email: null,
    password: null,
    globalSSOStatus: false,
    googleSSO: null,
    msftSSO: null,
    ssoAuthStarted: false,
  });
  const dispatch = useAppDispatch();

  const { email, password, ssoAuthStarted, globalSSOStatus, googleSSO, msftSSO } = state;
  const { session, isLoggingIn, isOrgSwitchLoading } = props;
  const isSSOAuthOnly = GOOGLE_SSO_ENABLED === false && BASIC_LOGIN_ENABLED === false;

  useEffect(() => {
    return () => dispatch(clearSessionError());
  }, []);

  useEffect(() => {
    if (GOOGLE_SSO_ENABLED === true) {
      /* fetch all open options from API/sign_on_options. then iterate over them to find the one whose proptocol is 'google' */
      const fetchSSOOptions = async () => {
        const response = await fetch(GET_ENV_SSO_OPTIONS_URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`could not fetch signon options ${response.status}`);
        }

        const ssoOptions = await response.json();
        setState({ globalSSOStatus: true });
        forEach(ssoOptions, (option) => {
          if (option?.protocol === 'google' && has(option, 'client_config.web.client_id')) {
            setState({ googleSSO: option });
          } else if (
            option?.protocol === 'oidc' &&
            (option?.uid === 'azure_entra_oidc' ||
              (has(option, 'client_config.idp') && option.client_config.idp === 'azure'))
          ) {
            setState({ msftSSO: option });
          }
        });
      };

      fetchSSOOptions().catch((e) => {
        console.error(e);
      });
    }
  }, []);

  useEffect(() => {
    if (
      GOOGLE_SSO_ENABLED === true &&
      ssoAuthStarted === false &&
      googleSSO &&
      has(googleSSO, 'client_config.web.client_id')
    ) {
      dispatch(initGoogleApi('signin', googleSSO?.client_config.web.client_id));
    }
  }, [googleSSO, ssoAuthStarted]);

  function setState(data) {
    set((fields) => ({
      ...fields,
      ...data,
    }));
  }

  function _handleLoginFormSubmit(e) {
    e.stopPropagation();
    e.preventDefault();
    props.loginWithUsername(state.email, state.password);
  }

  function _handleInputChanged(type, value) {
    setState({
      [type]: value,
    });
  }

  function _handleSSOSubmit(e) {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    _startSSOSession();
  }

  function _handleSSOStateToggled(e) {
    setState({
      ssoAuthStarted: !state.ssoAuthStarted,
    });
  }

  const _startSSOSession = () => {
    const { email } = state;
    const { location } = props;
    const nextPath =
      !isNil(location.state) &&
      has(location.state, 'from.pathname') &&
      startsWith(location.state.from.pathname, '/')
        ? location.state.from.pathname
        : '/';

    const ssoConfigUrl =
      isValidEmail(email) === true
        ? GET_USERS_IDP_URL + querystringify.stringify({ email: email }, true)
        : GET_USERS_IDP_URL + querystringify.stringify({ client_identifier: email }, true);

    fetch(ssoConfigUrl, {
      method: 'GET',
      cache: 'no-cache',
    })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          return response.json();
        } else {
          throw new Error(response.statusText);
        }
      })
      .then(
        (res) => {
          //TODO: Temp for assuming first valid IDP should be used
          const idp = !isEmpty(res) ? find(res, (r) => !isEmpty(r.client_config)) : null;
          if (isNil(idp)) {
            props.ssoAuthError(
              'This email is not configured for SSO. Please contact us for SSO configurations.',
            );
            // throw new Error('Please configure a valid IDP');
          } else {
            const data = idp.client_config;
            const isPkce =
              has(data, 'sso_type') && data.sso_type === SSO_AUTH_TYPES.oidc_pkce ? true : false;
            const tokenIdentifiers = generateTokenIdentifiers(isPkce);

            props.ssoIdpAuthStart({
              ssoType: data.sso_type,
              configInfo: data,
              nextPath,
              authParams: tokenIdentifiers,
            });
          }
        },
        (err) => {
          props.ssoAuthError();
        },
      )
      .catch((err) => {
        props.ssoAuthError();
      });
  };

  const _handleMSFTSubmit = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    _startMSFTSSOSession();
  };

  const _startMSFTSSOSession = () => {
    const { location } = props;
    const { msftSSO } = state;

    const idpData = msftSSO?.client_config;

    const nextPath =
      !isNil(location.state) &&
      has(location.state, 'from.pathname') &&
      startsWith(location.state.from.pathname, '/')
        ? location.state.from.pathname
        : '/';

    const tokenIdentifiers = generateTokenIdentifiers(true);

    props.ssoIdpAuthStart({
      ssoType: SSO_AUTH_TYPES.oidc_pkce,
      configInfo: idpData,
      nextPath,
      authParams: tokenIdentifiers,
    });
  };

  let homeComponent = <div></div>;
  if (isSSOAuthOnly) {
    //const isSSOAutoLogin = AUTO_LOGIN_ENABLED === true;
    homeComponent = (
      <div key="login" className="LoginForm">
        <SSOLoginForm
          email={email}
          singleStep={true}
          isSSOAutoLogin={false}
          ssoAuthStarted={ssoAuthStarted}
          onSSOStateToggle={_handleSSOStateToggled}
          onSubmit={_handleSSOSubmit}
          logout={props.logout}
          onInputChanged={_handleInputChanged}
        />
      </div>
    );
  } else {
    homeComponent = (
      <div key="login" className="LoginForm">
        {!ssoAuthStarted && (
          <>
            {BASIC_LOGIN_ENABLED && (
              <LoginForm
                loginErrorInfo={session.error}
                onSubmit={_handleLoginFormSubmit}
                email={email}
                password={password}
                onInputChanged={_handleInputChanged}
                signupMessage={''}
              />
            )}
            <div className="or-divider">
              <span className="divider-line" />
              <Typography variant="body">OR</Typography>
              <span className="divider-line" />
            </div>
            {globalSSOStatus && googleSSO && <GoogleLoginView />}
            {globalSSOStatus && msftSSO && <MicrosoftLoginView onSubmit={_handleMSFTSubmit} />}
          </>
        )}
        <SSOLoginForm
          email={email}
          singleStep={false}
          showCta={!ssoAuthStarted}
          onSSOStateToggle={_handleSSOStateToggled}
          onSubmit={_handleSSOSubmit}
          onInputChanged={_handleInputChanged}
        />
        <div className="tos-message">
          By signing in you agree to Nexla's{' '}
          <a
            href="https://www.nexla.com/terms-of-service/"
            target="_blank"
            rel="noopener noreferrer"
          >
            terms
          </a>{' '}
          and{' '}
          <a href="https://www.nexla.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
            privacy policy
          </a>
        </div>
        <Stack
          flex={1}
          marginTop="20px"
          justifyContent="center"
          width="100%"
          direction="row"
          gap="8px"
          alignItems="center"
        >
          <a
            href="https://www.express.dev/login"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', width: '100%' }}
            className="signup-link"
          >
            <Button variant="outlined" color="primary" style={{ width: '100%' }}>
              Sign Up
            </Button>
          </a>
          {/* NEX-20514: Commenting out in case we want to revert later to the original sign up link*/}
          {/* <Link replace to="/signup" style={{ textDecoration: 'none', width: '100%' }} className="signup-link">
            <Button variant="outlined" color="primary" style={{ width: '100%' }}>
              Sign Up
            </Button>
          </Link>  */}
        </Stack>
      </div>
    );
  }

  if (isOrgSwitchLoading) {
    return <OrgSwitchLoader />;
  } else {
    return HomeDecorator(homeComponent, session.error, isLoggingIn);
  }
}

export default LoginView;
